// apps/spa/src/components/audit/AuditLogRow.tsx
// Renders a single audit log entry as a table row with 5 columns:
//   Time (relative + absolute on Tooltip hover)
//   Officer (avatar + username)
//   Action (AuditActionBadge)
//   Target (entity link OR strikethrough for deleted/unknown entities)
//   ▾ (chevron button to expand AuditLogDiffPanel)
//
// Clicking the chevron expands AuditLogDiffPanel as a full-row spanning cell.
// Target link: /reminders/:id/edit OR /commands/:id/edit.
// Target strikethrough: when action is *.delete OR the targetId is not in knownTargetIds.

import { useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { EnrichedAuditLogEntry } from '@guardian/shared';
import { BASTN_TIMEZONE } from '@guardian/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { TableRow, TableCell } from '@/components/ui/table';
import { useTimeFormat } from '@/hooks/useTimeFormat';
import { AuditActionBadge } from './AuditActionBadge';
import { AuditLogDiffPanel } from './AuditLogDiffPanel';

interface AuditLogRowProps {
  entry: EnrichedAuditLogEntry;
  /**
   * Set of known (non-deleted) entity IDs from useReminders + useCommands caches.
   * Used to determine whether the Target column renders as a link or strikethrough.
   */
  knownTargetIds: Set<string>;
}

export function AuditLogRow({ entry, knownTargetIds }: AuditLogRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { hour12 } = useTimeFormat();

  const relative = formatRelative(entry.ts);
  const absolute = new Date(entry.ts).toLocaleString('en-US', {
    timeZone: BASTN_TIMEZONE,
    hour12,
  });

  // Entity is "deleted" if:
  //   a) The action itself is a delete action, OR
  //   b) The targetId is not in the live entity caches (was deleted later)
  const isDeleted =
    entry.action.endsWith('.delete') ||
    (Boolean(entry.targetId) && !knownTargetIds.has(entry.targetId!));

  const targetLabel = describeTarget(entry);
  const targetHref = isDeleted ? null : describeTargetHref(entry);

  const initials = entry.actor.username.slice(0, 2).toUpperCase();

  return (
    <>
      <TableRow>
        {/* Time column — relative label, absolute on Tooltip hover */}
        <TableCell>
          <Tooltip>
            <TooltipTrigger>
              <span className="cursor-default text-sm">{relative}</span>
            </TooltipTrigger>
            <TooltipContent>{absolute}</TooltipContent>
          </Tooltip>
        </TableCell>

        {/* Officer column — avatar + username */}
        <TableCell>
          <div className="flex items-center gap-2">
            <Avatar size="sm">
              {entry.actor.avatar_url && (
                <AvatarImage src={entry.actor.avatar_url} alt={entry.actor.username} />
              )}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <span className="text-sm">{entry.actor.username}</span>
          </div>
        </TableCell>

        {/* Action column */}
        <TableCell>
          <AuditActionBadge action={entry.action} />
        </TableCell>

        {/* Target column — link or strikethrough */}
        <TableCell>
          {targetLabel ? (
            targetHref ? (
              <Link
                to={targetHref}
                className="text-sm underline underline-offset-2 hover:opacity-80"
              >
                {targetLabel}
              </Link>
            ) : (
              <s className="text-sm text-muted-foreground" data-testid="deleted-target">
                {targetLabel}
              </s>
            )
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>

        {/* Expand chevron */}
        <TableCell>
          <Button
            variant="ghost"
            size="icon"
            aria-label={expanded ? 'Collapse row' : 'Expand row'}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
      </TableRow>

      {/* Expanded diff panel — spans all 5 columns */}
      {expanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30 py-3">
            <AuditLogDiffPanel entry={entry} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Formats a millisecond timestamp as a relative label: 'just now', 'N min ago', 'N h ago', 'N d ago'. */
function formatRelative(ts: number): string {
  const ms = Date.now() - ts;
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} h ago`;
  return `${Math.round(ms / 86_400_000)} d ago`;
}

/**
 * Extracts a human-readable target label from the entry.
 * Falls back to the raw targetId if no name is found in the payload.
 */
function describeTarget(entry: EnrichedAuditLogEntry): string | null {
  if (!entry.targetType || !entry.targetId) return null;

  let payload: Record<string, unknown> = {};
  try {
    if (entry.payloadJson) payload = JSON.parse(entry.payloadJson) as Record<string, unknown>;
  } catch {
    /* malformed payload — use targetId as fallback */
  }

  // Try to find entity name from payload before/after snapshots
  const name =
    (payload.before as Record<string, unknown> | undefined)?.name ??
    (payload.after as Record<string, unknown> | undefined)?.name ??
    payload.name ??
    entry.targetId;

  const prefix = entry.targetType === 'command' ? '/' : '';
  return `${entry.targetType} ${prefix}${name as string}`;
}

/**
 * Returns the SPA edit route for a non-deleted target entity.
 */
function describeTargetHref(entry: EnrichedAuditLogEntry): string | null {
  if (!entry.targetType || !entry.targetId) return null;
  if (entry.targetType === 'reminder') return `/reminders/${entry.targetId}/edit`;
  if (entry.targetType === 'command') return `/commands/${entry.targetId}/edit`;
  return null;
}
